import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { doc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { canAccessCity, findSupportedCity, getDefaultVisibleCity, getVisibleCities } from '../utils/cities';

const CITY_KEY_STORAGE = 'tonyte.selectedEventCityKey';
const DEFAULT_CITY = 'dresden';

interface CityContextType {
  selectedCityKey: string;
  setSelectedCityKey: (key: string) => void;
  visibleCities: ReturnType<typeof getVisibleCities>;
}

const CityContext = createContext<CityContextType>({
  selectedCityKey: DEFAULT_CITY,
  setSelectedCityKey: () => {},
  visibleCities: [],
});

export const CityProvider: React.FC<{ userId: string | null; role?: unknown; children: React.ReactNode }> = ({
  userId,
  role,
  children,
}) => {
  const [selectedCityKey, setSelectedCityKeyState] = useState(DEFAULT_CITY);
  const visibleCities = useMemo(() => getVisibleCities(role), [role]);
  const defaultCity = useMemo(() => getDefaultVisibleCity(role), [role]);

  useEffect(() => {
    AsyncStorage.getItem(CITY_KEY_STORAGE)
      .then(stored => {
        if (stored && canAccessCity(stored, role)) {
          setSelectedCityKeyState(stored);
        } else {
          setSelectedCityKeyState(defaultCity.value);
          AsyncStorage.setItem(CITY_KEY_STORAGE, defaultCity.value).catch(() => {});
        }
      })
      .catch(() => {});
  }, [defaultCity.value, role]);

  useEffect(() => {
    if (canAccessCity(selectedCityKey, role)) return;
    setSelectedCityKeyState(defaultCity.value);
    AsyncStorage.setItem(CITY_KEY_STORAGE, defaultCity.value).catch(() => {});
    if (userId) {
      setDoc(
        doc(firestore, 'users', userId),
        { selectedEventCity: defaultCity.label, selectedEventCityKey: defaultCity.value },
        { merge: true },
      ).catch(() => {});
    }
  }, [defaultCity.label, defaultCity.value, role, selectedCityKey, userId]);

  const setSelectedCityKey = useCallback(
    (key: string) => {
      const nextCity = canAccessCity(key, role) ? findSupportedCity(key) : defaultCity;
      if (!nextCity) return;
      setSelectedCityKeyState(nextCity.value);
      AsyncStorage.setItem(CITY_KEY_STORAGE, nextCity.value).catch(() => {});
      if (userId) {
        setDoc(
          doc(firestore, 'users', userId),
          { selectedEventCity: nextCity.label, selectedEventCityKey: nextCity.value },
          { merge: true },
        ).catch(() => {});
      }
    },
    [defaultCity, role, userId],
  );

  const value = useMemo(
    () => ({ selectedCityKey, setSelectedCityKey, visibleCities }),
    [selectedCityKey, setSelectedCityKey, visibleCities],
  );

  return (
    <CityContext.Provider value={value}>
      {children}
    </CityContext.Provider>
  );
};

export const useCityContext = () => useContext(CityContext);
